import { useState } from 'react'
import { Accordion, Button, Card, Grid, Input } from '@design-systems/canvas-ui'
import {
    getTimeDimensionTranslationKey,
    getTimeDimensionLabelKey,
} from './helpers'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { useEssentials } from 'hooks/utils/useEssentials'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useLogClick } from 'utils/analytics'
import { EnrichedMetric } from './types'

const buildDetailsUrl = (
    metricName: string,
    tabName: string | undefined,
    searchParams: URLSearchParams,
): string => {
    const params = new URLSearchParams()
    params.set('tab', tabName || 'guest')
    const locationId = searchParams.get('locationId')
    if (locationId) params.set('locationId', locationId)
    const type = searchParams.get('type')
    if (type) params.set('type', type)
    return `/mytarget-performance/details/${encodeURIComponent(metricName)}?${params.toString()}`
}

interface MetricCardProps {
    metric: EnrichedMetric
    onHistoricalDetailsClick: () => void
    showSeeDetails?: boolean
    tabName?: string
}

export const MetricCard = ({
    metric,
    onHistoricalDetailsClick,
    showSeeDetails = false,
    tabName,
}: MetricCardProps) => {
    const { t } = useEssentials()
    const { myTargetPerformance } = TRANSLATION_KEYS
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const { logClick } = useLogClick('myTarget-performance')

    const [isHistoricalOpen, setIsHistoricalOpen] = useState(false)

    return (
        <div className="guest-metric-card-wrapper">
            <Card className="hc8-ta-center hc8-ma-none hc8-pa-none">
                <div
                    className={`operations-metric-tile-header guest-metric-header ${metric.colorClass}`}
                >
                    {metric.displayName}
                </div>

                {metric.goalValue && (
                    <div className="hc8-ta-right hc8-pt-2x">
                        <Input.Chip size="sm">
                            {`${t(myTargetPerformance.goal)}: ${metric.goalValue.value}`}
                        </Input.Chip>
                    </div>
                )}

                <div className="hc8-fs-050 guest-metric-time-label">
                    {t(getTimeDimensionLabelKey(metric.primary_time_dimension))}
                </div>

                <div
                    className={`hc8-fs-050 guest-metric-value ${metric.textColorClass}`}
                >
                    {metric.metricValue?.value || '-'}
                </div>

                {metric.historicalValues.length > 0 && (
                    <div className="guest-metric-expandable-wrapper">
                        <Accordion
                            spacing="dense"
                            isOpen={isHistoricalOpen}
                            onChange={() => {
                                setIsHistoricalOpen((prev) => {
                                    const next = !prev
                                    if (next) {
                                        onHistoricalDetailsClick()
                                    }
                                    return next
                                })
                            }}
                            headerSlot={
                                <p className="guest-historical-header">
                                    {t(myTargetPerformance.historicalDetails)}
                                </p>
                            }
                        >
                            <div className="hc8-pt-4x">
                                {metric.historicalValues.map((hist) => (
                                    <Grid.Container
                                        key={hist.timeDim}
                                        className="hist-row"
                                    >
                                        <Grid.Item
                                            className="hist-row__title"
                                            title={t(hist.translationKey)}
                                        >
                                            {t(hist.translationKey)}
                                        </Grid.Item>
                                        <Grid.Item
                                            className={`hist-row__value ${hist.colorClass}`}
                                        >
                                            {hist.value?.value || '-'}
                                        </Grid.Item>
                                    </Grid.Container>
                                ))}
                            </div>
                        </Accordion>
                    </div>
                )}

                {metric.secondaryTimeDimensionValue.timeDimension && (
                    <div className="hc8-pt-4x">
                        <Grid.Container>
                            <Grid.Item
                                className="hc8-fs-050 hc8-pb-2x hc8-pt-none"
                                title={t(
                                    getTimeDimensionTranslationKey(
                                        metric.secondaryTimeDimensionValue
                                            .timeDimension,
                                    ),
                                )}
                                size={{
                                    xs: 12,
                                }}
                            >
                                {t(
                                    getTimeDimensionLabelKey(
                                        metric.secondaryTimeDimensionValue
                                            .timeDimension,
                                    ),
                                )}
                            </Grid.Item>
                            <Grid.Item
                                className={`${metric.secondaryTimeDimensionValue.colorClass} hc8-fs-200 hc8-pt-none`}
                                size={{
                                    xs: 12,
                                }}
                            >
                                {metric.secondaryTimeDimensionValue.value
                                    ?.value || '-'}
                            </Grid.Item>
                        </Grid.Container>
                    </div>
                )}

                {showSeeDetails && (
                    <Grid.Item
                        size={{
                            xs: 12,
                        }}
                    >
                        <Button
                            className={'hc8-fs-100'}
                            aria-label={`${t(myTargetPerformance.seeDetails)} ${metric.displayName}`}
                            onClick={() => {
                                logClick(
                                    `mytargetperformance details ${metric.displayName}`,
                                )
                                void navigate(
                                    buildDetailsUrl(
                                        metric.metric_name,
                                        tabName,
                                        searchParams,
                                    ),
                                    { state: { metric } },
                                )
                            }}
                            variant="ghost"
                        >
                            {t(myTargetPerformance.seeDetails)}
                        </Button>
                    </Grid.Item>
                )}
            </Card>
        </div>
    )
}
